import { Switch, Route, Redirect } from 'react-router-dom';

import ProtectedRoute from '@Components/ProtectedRoute';
import AccountSettingsPage from '@Pages/accountSettings';
import SMTPSettingsPage from '@Pages/emails/SMTPSettingsPage.component';
import SMTPSettingsVerificationPage from '@Pages/emails/SMTPSettingsVerificationPage.component';
import { AdminGroupsPage } from '@Pages/groups';
import {
  EmailsActivePage,
  EmailsArchivedPage,
  LoginPage,
  DncPage,
  GlobalDncPage,
  NotFound,
  ScriptsActivePage,
  ScriptsArchivedPage,
  ReportPage,
  UserCallerIdsPage,
  AdminReportPage,
  UsersPage,
  AdminScriptsPage,
  AdminEmailsPage,
  CallerIdCheckerPage,
} from '@Pages/index';
import AdminSettingsPage from '@Pages/settings';
import { useAppSelector } from '@Utils/hooks/useStore.hook';

import { RoutesConstants, CommonConstants } from './constants';

const AppRoutes = (): JSX.Element => {
  // as soon as the isLoggedIn flag is set to false in the state, it will automatically redirect to login
  // no need to explicitly route the user using history.push
  const { isLoggedIn, role } = useAppSelector((state) => state.auth);

  return (
    <Switch>
      <Route
        exact
        path={RoutesConstants.GLOBAL_ROUTES.LOGIN}
        component={LoginPage}
      />
      <Route
        exact
        path={RoutesConstants.GLOBAL_ROUTES.SSO}
        component={LoginPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.ADMIN_ROUTES.USERS}
        requiredRoles={[CommonConstants.USER_ROLES.ADMIN]}
        component={UsersPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.ADMIN_ROUTES.REPORT}
        requiredRoles={[CommonConstants.USER_ROLES.ADMIN]}
        component={AdminReportPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.ADMIN_ROUTES.USER_CALLER_IDS}
        requiredRoles={[CommonConstants.USER_ROLES.USER]}
        component={UserCallerIdsPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.SCRIPTS_ROUTES.ACTIVE}
        requiredRoles={[CommonConstants.USER_ROLES.USER]}
        component={ScriptsActivePage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.SCRIPTS_ROUTES.ARCHIVED}
        requiredRoles={[CommonConstants.USER_ROLES.USER]}
        component={ScriptsArchivedPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.ADMIN_ROUTES.GLOBAL_SCRIPTS}
        requiredRoles={[CommonConstants.USER_ROLES.ADMIN]}
        component={AdminScriptsPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.DNC_ROUTES.BASE}
        requiredRoles={[CommonConstants.USER_ROLES.USER]}
        component={DncPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.ADMIN_ROUTES.GLOBAL_DNC}
        requiredRoles={[CommonConstants.USER_ROLES.ADMIN]}
        component={GlobalDncPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.EMAILS_ROUTES.ACTIVE}
        requiredRoles={[
          CommonConstants.USER_ROLES.ADMIN,
          CommonConstants.USER_ROLES.USER,
        ]}
        component={EmailsActivePage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.EMAILS_ROUTES.ARCHIVED}
        requiredRoles={[
          CommonConstants.USER_ROLES.ADMIN,
          CommonConstants.USER_ROLES.USER,
        ]}
        component={EmailsArchivedPage}
      />
      <ProtectedRoute
        exact
        component={SMTPSettingsPage}
        path={RoutesConstants.EMAILS_ROUTES.SMTP}
        requiredRoles={[CommonConstants.USER_ROLES.USER]}
      />
      <ProtectedRoute
        exact
        component={SMTPSettingsVerificationPage}
        path={RoutesConstants.EMAILS_ROUTES.SMTP_VERIFICATION}
        requiredRoles={[CommonConstants.USER_ROLES.USER]}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.REPORT_ROUTES.BASE}
        requiredRoles={[CommonConstants.USER_ROLES.USER]}
        component={ReportPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.ADMIN_ROUTES.GROUPS}
        requiredRoles={[CommonConstants.USER_ROLES.ADMIN]}
        component={AdminGroupsPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.ADMIN_ROUTES.GLOBAL_EMAILS}
        requiredRoles={[CommonConstants.USER_ROLES.ADMIN]}
        component={AdminEmailsPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.ADMIN_ROUTES.ADMIN_SETTINGS}
        requiredRoles={[CommonConstants.USER_ROLES.ADMIN]}
        component={AdminSettingsPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.ACCOUNT_SETTINGS_ROUTES.BASE}
        requiredRoles={[
          CommonConstants.USER_ROLES.ADMIN,
          CommonConstants.USER_ROLES.USER,
        ]}
        component={AccountSettingsPage}
      />
      <ProtectedRoute
        exact
        path={RoutesConstants.ADMIN_ROUTES.CALLER_IDS_CHECKER}
        requiredRoles={[CommonConstants.USER_ROLES.ADMIN]}
        component={CallerIdCheckerPage}
      />
      <Route exact path="/">
        {isLoggedIn ? (
          <Redirect
            to={
              role === CommonConstants.USER_ROLES.USER
                ? RoutesConstants.SCRIPTS_ROUTES.ACTIVE
                : RoutesConstants.ADMIN_ROUTES.USERS
            }
          />
        ) : (
          <Redirect to={RoutesConstants.GLOBAL_ROUTES.LOGIN} />
        )}
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
};

export default AppRoutes;
